import { useCallback, useEffect, useRef } from 'react';
import { MutationKey, useMutation } from '@tanstack/react-query';
import {
	markMutationPending,
	unmarkMutationPending,
} from '@dealstack/frontend/shared/hooks/pendingMutations';

interface UseDebouncedSaveOptions<TResponse> {
	mutationKey: MutationKey;
	debounceMs: number;
	mutationFn: () => Promise<TResponse | null>;
	/** Fires only when the queue has fully drained (no follow-up run queued and
	 * no debounce-pending change). Intermediate completions are swallowed so
	 * cache-replace logic in callers can't clobber unsaved user input. */
	onSuccess?: (response: TResponse | null) => void;
	onError?: (error: unknown) => void;
}

interface UseDebouncedSaveReturn {
	schedule: () => void;
	flush: () => Promise<boolean>;
	cancel: () => void;
}

/**
 * Debounced + serialized save. `schedule()` resets the debounce; while a
 * mutation is in-flight, a follow-up runs as soon as it resolves so callers'
 * side effects (e.g. CREATE returning a new id) are preserved. In-flight saves
 * are never aborted by typing; `cancel()` and unmount drop the timer + queue
 * but let in-flight requests finish.
 */
export function useDebouncedSave<TResponse>(
	options: UseDebouncedSaveOptions<TResponse>
): UseDebouncedSaveReturn {
	const optionsRef = useRef(options);
	optionsRef.current = options;

	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const inflightRef = useRef(false);
	const queuedRef = useRef(false);
	const runPromiseRef = useRef<Promise<boolean> | null>(null);
	// Remember the key we marked with so we unmark the same one even if
	// options.mutationKey churns mid-flight.
	const pendingKeyRef = useRef<MutationKey | null>(null);
	const latestKeyRef = useRef<MutationKey>(options.mutationKey);
	latestKeyRef.current = options.mutationKey;

	const markPending = useCallback(() => {
		if (pendingKeyRef.current === null) {
			pendingKeyRef.current = latestKeyRef.current;
			markMutationPending(pendingKeyRef.current);
		}
	}, []);

	const clearPending = useCallback(() => {
		if (pendingKeyRef.current !== null) {
			unmarkMutationPending(pendingKeyRef.current);
			pendingKeyRef.current = null;
		}
	}, []);

	// onSuccess is gated below in runMutation's finally — only fires once the
	// queue has fully drained, so it's not wired into useMutation directly.
	const mutation = useMutation({
		mutationKey: options.mutationKey,
		mutationFn: () => optionsRef.current.mutationFn(),
		onError: (error) => optionsRef.current.onError?.(error),
	});

	const runMutation = useCallback((): Promise<boolean> => {
		if (inflightRef.current) {
			// Only queue a follow-up for an actually-pending change. Queueing
			// unconditionally (e.g. a flush that just wants to await the in-flight save)
			// adds a no-op run AND suppresses the real save's onSuccess, which is gated on
			// nothing being queued.
			if (pendingKeyRef.current !== null) {
				queuedRef.current = true;
			}
			return runPromiseRef.current ?? Promise.resolve(true);
		}

		inflightRef.current = true;
		const run = async (): Promise<boolean> => {
			let allSucceeded = true;

			do {
				queuedRef.current = false;
				clearPending();
				let response: TResponse | null = null;
				let succeeded = false;
				try {
					response = (await mutation.mutateAsync()) ?? null;
					succeeded = true;
				} catch {
					// useMutation already routed this to onError; keep draining so a
					// newer snapshot is not stranded behind a failed save.
					allSucceeded = false;
				}

				if (!queuedRef.current && succeeded && pendingKeyRef.current === null) {
					optionsRef.current.onSuccess?.(response);
				}
			} while (queuedRef.current);

			return allSucceeded;
		};

		const promise = run().finally(() => {
			inflightRef.current = false;
			if (runPromiseRef.current === promise) {
				runPromiseRef.current = null;
			}
		});
		runPromiseRef.current = promise;
		return promise;
	}, [mutation, clearPending]);

	const submit = useCallback(() => {
		timerRef.current = null;
		void runMutation();
	}, [runMutation]);

	const schedule = useCallback(() => {
		markPending();
		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}
		timerRef.current = setTimeout(submit, optionsRef.current.debounceMs);
	}, [submit, markPending]);

	const flush = useCallback(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
		if (pendingKeyRef.current === null && !inflightRef.current) {
			return Promise.resolve(true);
		}
		return runMutation();
	}, [runMutation]);

	const cancel = useCallback(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
		queuedRef.current = false;
		clearPending();
	}, [clearPending]);

	useEffect(() => {
		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}
			queuedRef.current = false;
			clearPending();
		};
	}, [clearPending]);

	return { schedule, flush, cancel };
}
